import {useQuery} from 'react-query';
import {
    DEFAULT_OPTIONAL_FIELDS,
    DETAILS_QUERY_KEY,
    INACTIVE_FIELD_NAME,
    INITIAL_QUERY,
    INV_COLUMNS_QUERY_KEY,
    PAGES_COUNT_QUERY,
    PART_INVENTORY_FIELDS,
    PART_PARENT_DEFAULT_FIELDS,
    PARTS_FORM_NAME,
    START_COUNT_FORM_QUERY,
    START_FORM_QUERY,
    TQUANT_FIELD_NAME
} from '../consts';
import {
    fetchFamilyOptions,
    fetchInventoryColumns,
    fetchPagesCount,
    fetchParts,
    fetchPartsColumns,
    getSelectedFields
} from '../service';
import {startForm} from '../../../services/form/form';
import {partsStore} from '../PartsStore';
import {buildFilters} from '../utils';
import {useEffect} from 'react';
import {serializeFamilyOptions, serializePartsDetails} from '../serialize';
import {getEditingColumns} from '../actions';

interface SearchParams {
    query: string;
    selectedFamilies: string[];
    page: number;
    selectedColumns?: string[];
}
const fetchInitialData = async (formName: string) => {
    const res = await Promise.all([fetchFamilyOptions(), getSelectedFields(formName)]);
    const [families, selectedColumnsRes] = res;
    return {
        families,
        selectedColumnsRes
    };
};

export const usePartsQuery = (formName: string, searchParams: SearchParams) => {
    const {
        selectedColumns,
        setFamilies,
        setSelectedColumns,
        setQuantitySelector,
        setEditingColumns,
        setPartsDetails,
        setSession,
        setPagesCount,
        selectedItems
    } = partsStore;

    const startFormAndGetColumns = async () => {
        const res = await startForm(PARTS_FORM_NAME, null, [INACTIVE_FIELD_NAME]);
        const columns = await fetchPartsColumns(res.Session);
        return {session: res.Session, columns};
    };
    //session for items
    const {isLoading: isLoadingStartForm, data: form} = useQuery(
        [START_FORM_QUERY],
        startFormAndGetColumns,
        {
            onSuccess: data => {
                setSession(data.session);
            },
            cacheTime: 0
        }
    );

    //session for count
    const {isLoading: isLoadingStartFormCount, data: countForm} = useQuery(
        [START_COUNT_FORM_QUERY],
        () => startForm(PARTS_FORM_NAME, {keepFocus: true}, [INACTIVE_FIELD_NAME]),
        {
            cacheTime: 0
        }
    );

    //fetch initial data - start part form twice for count and part session
    //get families and selected columns
    const {isLoading: isLoadingInitialData} = useQuery(
        [INITIAL_QUERY],
        () => fetchInitialData(formName),
        {
            onSuccess: data => {
                const {selectedColumnsRes, families} = data;
                const serializedFamilies = serializeFamilyOptions(
                    families?.records,
                    families?.columns
                );
                setFamilies(serializedFamilies);
                setSelectedColumns(selectedColumnsRes.selectedColumnsRes);
                setQuantitySelector(selectedColumnsRes.quantSelectorRes);
            },
            cacheTime: 0
        }
    );

    //fetch parts details
    const {
        isLoading: isLoadingPartsDetails,
        data: partsRecords,
        isFetching
    } = useQuery(
        [DETAILS_QUERY_KEY, form?.session, searchParams],
        () => {
            const filters = buildFilters(
                form.columns,
                selectedColumns,
                searchParams.query,
                searchParams.selectedFamilies
            );
            //////////////
            const columnsIDs = form.columns.reduce((acc, value) => {
                if (
                    [
                        ...partsStore.selectedColumns,
                        ...PART_INVENTORY_FIELDS,
                        TQUANT_FIELD_NAME,
                        ...DEFAULT_OPTIONAL_FIELDS,
                        ...PART_INVENTORY_FIELDS,
                        ...PART_PARENT_DEFAULT_FIELDS
                    ].includes(value.Name) ||
                    value.Key
                ) {
                    acc.push(value.ID);
                }
                return acc;
            }, []);
            ////////////////
            return fetchParts(form?.session, searchParams.page, filters, columnsIDs);
        },
        {
            enabled: !!form?.session && !!form?.columns?.length,
            keepPreviousData: true,
            onSuccess: records => {
                const parts = serializePartsDetails(records, form?.columns, selectedItems);
                setPartsDetails(parts);
            }
        }
    );

    //fetch inventory columns data - should execute only once
    const {isLoading: isLoadingInventoryColumns, data: inventoryColumns} = useQuery(
        [INV_COLUMNS_QUERY_KEY, form?.session],
        () => fetchInventoryColumns(form?.session, partsRecords?.[0]?.Fields),
        {
            enabled: !!partsRecords?.length,
            cacheTime: 0
        }
    );

    useEffect(() => {
        if (form?.columns?.length && inventoryColumns) {
            const editingColumns = getEditingColumns(form?.columns, inventoryColumns);
            setEditingColumns(editingColumns);
        }
    }, [form?.columns, inventoryColumns]);

    //fetch page count
    const countSession = countForm?.Session;
    const {isFetching: isLoadingPageCount} = useQuery(
        [PAGES_COUNT_QUERY, countSession, searchParams.query, searchParams.selectedFamilies],
        async () => {
            const filters = buildFilters(
                form?.columns,
                selectedColumns,
                searchParams.query,
                searchParams.selectedFamilies
            );
            return fetchPagesCount(countSession, filters);
        },
        {
            enabled: !!countSession && !!form?.columns?.length,
            keepPreviousData: false,
            onSuccess: data => {
                setPagesCount(data);
            }
        }
    );

    return {
        isLoadingPageCount,
        isLoadingPartsDetails: isFetching || isLoadingPartsDetails,
        isLoadingInitialData:
            isLoadingInitialData ||
            isLoadingInventoryColumns ||
            isLoadingStartForm ||
            isLoadingStartFormCount
    };
};
